/**
 * Calendar-date helpers.
 *
 * A scheduled date is a day on the calendar, not an instant. The API sends
 * DATE columns as 'YYYY-MM-DD' (or 'YYYY-MM-DDT00:00:00.000Z' from routes
 * that pass node-pg's Date straight through); the first ten characters ARE
 * the day. Never turn one into a key with toISOString() or read it with
 * new Date('YYYY-MM-DD'): both work in UTC, so in BST a local-midnight cell
 * keyed with toISOString() became the previous day and every job on the
 * month view drew a day late, and west of UTC new Date('YYYY-MM-DD') shows
 * the day before.
 *
 * Days built here always come from the local year/month/day fields at local
 * midnight, so no time of day is ever carried and a clock change cannot move
 * a day.
 *
 * DATE values only. A timestamp (TIMESTAMPTZ, e.g. a ticket's opened_at) is an
 * instant: its first ten characters are the UTC day, so '2026-09-14T23:30:00Z'
 * (00:30 BST on the 15th) would read as the 14th. Never pass one to dateKeyOf,
 * fromDateKey or formatDateKey - format instants with utils/format.js. A field
 * that mixes both (the Jobs list's work_date) must be split by source first.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const pad = (n) => String(n).padStart(2, '0');

// 'YYYY-MM-DD' for the day this Date falls on in the viewer's own calendar.
export function toDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// The calendar day an API DATE field names, as 'YYYY-MM-DD' ('' when absent).
// Not for timestamps - see the note at the top.
export function dateKeyOf(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : toDateKey(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
}

// Local midnight on the day a 'YYYY-MM-DD' key (or API date value) names.
export function fromDateKey(value) {
  const key = dateKeyOf(value);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

// Same day of the month, clamped to the target month's length (31 Jan -> 28 Feb).
export function addMonths(date, months) {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + months + 1, 0).getDate();
  return new Date(date.getFullYear(), date.getMonth() + months, Math.min(date.getDate(), lastDay));
}

// Weeks start on Monday.
export function startOfWeek(date) {
  return addDays(date, -((date.getDay() + 6) % 7));
}

export function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

// The six Monday-first weeks a month calendar shows: from the Monday on or
// before the 1st (the 1st itself when it is a Monday), 42 days.
export function monthGridDays(date) {
  const first = startOfWeek(startOfMonth(date));
  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
}

// Whole calendar days from one date to another, unaffected by clock changes.
export function daysBetween(from, to) {
  const utcDay = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utcDay(to) - utcDay(from)) / 86400000);
}

// Whole calendar days from the viewer's today to the day a DATE field names:
// 0 on the day itself, negative once it has passed, null when absent. Not
// (due - now) / 24h, which counts from UTC midnight and flips mid-day.
export function daysFromToday(value, now = new Date()) {
  const date = fromDateKey(value);
  return date ? daysBetween(now, date) : null;
}

// en-GB display of an API date field, read as a calendar day in every zone.
export function formatDateKey(value, options, fallback = '-') {
  const date = fromDateKey(value);
  return date ? date.toLocaleDateString('en-GB', options) : fallback;
}

/**
 * ---------------------------------------------------------------------------
 * PAY DAYS: today in the UK (ONCALL_PAY_FIXES_DESIGN §8, commit B2)
 * ---------------------------------------------------------------------------
 *
 * The business runs on UK days. Pay periods, salary rises and rate changes all
 * start at 00:00 Europe/London, so a form that offers "today" must offer the UK
 * day whatever clock the browser is on:
 *  - toISOString gives the UTC day, so between midnight and 01:00 BST it offers
 *    YESTERDAY - inside a month Finance may already have paid;
 *  - the browser's own local day is right only for a browser in the UK.
 *
 * ukTodayKey asks Intl for the Europe/London date, so it is right in Auckland
 * and in New York too. en-CA gives YYYY-MM-DD; a browser without the zone data
 * falls back to the local day rather than showing nothing.
 */
export function ukTodayKey(now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(now);
  } catch {
    return toDateKey(now);
  }
}

/**
 * A date field that shows today's UK date until the person edits it, and
 * refreshes when the page comes back to them - a payroll screen left open over
 * midnight used to save a rise dated yesterday.
 *
 * Returns { value, setValue, refresh, reset, edited }:
 *  - setValue marks the field edited, and it is then left alone;
 *  - refresh() returns the value to submit, recomputing an untouched field, so
 *    a form open since before midnight still sends today;
 *  - reset() clears the edit, for a form cleared after saving.
 */
export function useUkTodayDefault() {
  const [value, setValue] = useState(ukTodayKey);
  const state = useRef({ edited: false, value });
  state.current.value = value;

  const refresh = useCallback(() => {
    if (state.current.edited) return state.current.value;
    const key = ukTodayKey();
    state.current.value = key;
    setValue(key);
    return key;
  }, []);

  const setFromUser = useCallback((next) => {
    state.current.edited = true;
    state.current.value = next;
    setValue(next);
  }, []);

  const reset = useCallback(() => {
    state.current.edited = false;
    return refresh();
  }, [refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  return { value, setValue: setFromUser, refresh, reset, edited: () => state.current.edited };
}

/**
 * The nights a late payment is for, as a person would say them (B4, US2).
 *
 * One night is a night, not 'nights', and a list of three is read out with
 * commas and a final 'and'. This is the line an engineer checks their own wage
 * against, so 'for the 27 Sep nights' or 'A and B and C nights' reads as a bug
 * in the figures.
 *
 * @param {string[]} nights - night labels, e.g. ['27 Sep', '29 Sep']
 * @returns {string} '' when there are none, otherwise 'for the ... night(s)'
 */
export function nightsPhrase(nights) {
  const list = (nights || []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return `for the ${list[0]} night`;
  const last = list[list.length - 1];
  return `for the ${list.slice(0, -1).join(', ')} and ${last} nights`;
}
